import { Box, useTheme } from "@mui/material";
import React, { useEffect, useMemo } from "react";

export interface FittedText {
  content: string;

  color?: string;

  // Optional CSS formatted height. If not provided, will be 100%
  height?: string;

  fontWeight?: string;

  horizontalAlignment?: "center" | "left" | "right";
  verticalAlignment?: "center" | "top" | "bottom";

  prefix?: string;
  prefixSize?: string;
  postfix?: string;
  postfixSize?: string;
}

export default function FittedText(props: FittedText): React.JSX.Element {
  const theme = useTheme();

  const { content } = props;
  const prefixSize = props.prefixSize || "0.7em";
  const postfixSize = props.postfixSize || "0.7em";

  const [aspectRatio, setAspectRatio] = React.useState<number | undefined>();

  useEffect(() => {
    const rootNode = document.createElement("span");
    rootNode.style.position = "fixed";
    rootNode.style.display = "inline";
    rootNode.style.visibility = "hidden";
    rootNode.style.top = "100%";
    rootNode.style.top = "0";
    rootNode.style.left = "0";
    rootNode.style.lineHeight = "1";
    rootNode.style.whiteSpace = "nowrap";
    rootNode.style.fontSize = "100px";
    rootNode.style.zIndex = "9999999";
    rootNode.style.fontWeight = props.fontWeight || "bold";
    document.body.appendChild(rootNode);

    if (props.prefix) {
      const prefixNode = document.createElement("span");
      rootNode.appendChild(prefixNode);
      prefixNode.innerText = props.prefix;
      prefixNode.style.fontSize = prefixSize;
    }

    const contentNode = document.createElement("span");
    rootNode.appendChild(contentNode);
    contentNode.innerText = content;

    if (props.postfix) {
      const postfixNode = document.createElement("span");
      rootNode.appendChild(postfixNode);
      postfixNode.innerText = props.postfix
      postfixNode.style.fontSize = postfixSize;
    }

    const rootRect = rootNode.getBoundingClientRect();
    const rootHeight = rootRect.height;
    const rootWidth = rootRect.width;

    setAspectRatio(rootWidth / (rootHeight-10));

    document.body.removeChild(rootNode);
  }, [props.content, content]);

  const preserveAspectRatioValue = useMemo(() => {
    let alignment = "xMid";
    if (props.horizontalAlignment === "right") {
      alignment = "xMax";
    } else if (props.horizontalAlignment === "left") {
      alignment = "xMin";
    }
    if (props.verticalAlignment === "top") {
      alignment += "YMin";
    } else if (props.verticalAlignment === "bottom") {
      alignment += "YMax";
    } else {
      alignment += "YMid";
    }
    return alignment;
  }, [props.horizontalAlignment, props.verticalAlignment]);

  return (
    <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: props.height || "100%" }}>
      <svg width="100%" viewBox={`0 0 ${(aspectRatio || 1)*10} 10`} style={{height: "100%", maxHeight: "100%"}}
        preserveAspectRatio={preserveAspectRatioValue}>
        <text x="50%" y="50%"
          dy="3"
          textAnchor="middle" fontSize="10" fontWeight={props.fontWeight || "bold"}
          fill={props.color || theme.palette.text.primary} style={{lineHeight: "100%"}}>
          {props.prefix &&
            <tspan fontSize={prefixSize}>{props.prefix}</tspan>
          }
          <tspan>{content}</tspan>
          {props.postfix &&
            <tspan fontSize={postfixSize}>{props.postfix}</tspan>
          }
        </text>
      </svg>
    </Box>
  );
}
